import { initializeApp, getApps } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { initializeFirestore, connectFirestoreEmulator } from "firebase/firestore";
import { getStorage, connectStorageEmulator } from "firebase/storage";

class FirebaseConfigService {
  constructor() {
    this.app = null;
    this.auth = null;
    this.firestore = null;
    this.storage = null;
    this.initialized = false;
    this.isDevelopment = false;
    this.emulatorsConnected = false;
  }

  /**
   * Initialize Firebase with environment-aware configuration
   * @returns {Object} Firebase services (app, auth, firestore, storage)
   */
  initialize() {
    if (this.initialized) {
      return this.getServices();
    }

    const env = process.env.REACT_APP_FIREBASE_ENV;
    const isDevelopment = env === "development";
    this.isDevelopment = isDevelopment;
    // final decision: only allow emulator when running in development
    const useEmulator = isDevelopment;
    const isStaging = env === "staging";
    const isProduction = env === "production";

    console.log(
      `Initializing Firebase in the ${env || "PRODUCTION"} mode`,
      { useEmulator, isDevelopment, isStaging, isProduction }
    );

    // Allow minimal/default values when using emulators during local dev
    const emulatorDefaults = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "demo-key",
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "localhost",
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "demo-project",
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "demo-project.appspot.com",
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "000000000000",
      appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:000000000000:web:000000000000",
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || undefined
    };

    const stagingConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY_STAGING,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN_STAGING,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID_STAGING,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET_STAGING,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID_STAGING,
      appId: process.env.REACT_APP_FIREBASE_APP_ID_STAGING,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID_STAGING
    };

    const prodConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
      measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
    };

    const firebaseConfig = isDevelopment ? emulatorDefaults : isStaging ? stagingConfig : prodConfig;

    // Validate configuration for production and staging builds
    if ((isStaging || isProduction) && (!firebaseConfig.apiKey || !firebaseConfig.projectId)) {
      throw new Error(`Firebase configuration is incomplete for ${env || "production"} environment. Please check your environment variables.`);
    }

    // Initialize app if not already initialized (supports HMR)
    if (!this.app) {
      if (!getApps().length) {
        this.app = initializeApp(firebaseConfig);
      } else {
        this.app = getApps()[0];
      }
    }

    // Initialize services
    this.auth = getAuth(this.app);
    // ✅ Use initializeFirestore with experimentalForceLongPolling to fix ERR_QUIC_PROTOCOL_ERROR
    this.firestore = initializeFirestore(this.app, {
      experimentalForceLongPolling: true,
    });
    this.storage = getStorage(this.app);

    // Connect to emulators if requested (only possible when running in development)
    if (useEmulator) {
      try {
        const authEmuUrl = process.env.REACT_APP_FIREBASE_EMULATOR_AUTH_URL || "http://localhost:9099";
        // connectAuthEmulator expects the full url and no trailing slash
        connectAuthEmulator(this.auth, authEmuUrl.replace(/\/$/, ""), { disableWarnings: true });

        const firestoreHost = process.env.REACT_APP_FIREBASE_EMULATOR_FIRESTORE_HOST || "localhost";
        const firestorePort = parseInt(process.env.REACT_APP_FIREBASE_EMULATOR_FIRESTORE_PORT || "8080", 10);
        connectFirestoreEmulator(this.firestore, firestoreHost, firestorePort);

        const storageHost = process.env.REACT_APP_FIREBASE_EMULATOR_STORAGE_HOST || "localhost";
        const storagePort = parseInt(process.env.REACT_APP_FIREBASE_EMULATOR_STORAGE_PORT || "9199", 10);
        connectStorageEmulator(this.storage, storageHost, storagePort);

        this.emulatorsConnected = true;
        console.log("Connected Firebase SDK to local emulators", {
          authEmuUrl,
          firestoreHost,
          firestorePort,
          storageHost,
          storagePort
        });
      } catch (err) {
        console.warn("Failed to connect to one or more Firebase emulators:", err);
      }
    }

    this.initialized = true;
    return this.getServices();
  }

  /**
   * Get Firebase services
   * @returns {Object} Firebase services
   */
  getServices() {
    if (!this.initialized) {
      return this.initialize();
    }
    return {
      app: this.app,
      auth: this.auth,
      firestore: this.firestore,
      storage: this.storage,
      usingEmulators: this.emulatorsConnected
    };
  }

  /**
   * Check if emulators are being used
   * @returns {boolean} True if using emulators
   */
  isUsingEmulators() {
    return this.emulatorsConnected;
  }
}

// Export singleton instance
const firebaseConfigService = new FirebaseConfigService();
export default firebaseConfigService;